import { Button } from 'blaze-ui/Button'
import Styles from './CaptionTab.module.scss'
import { useEffect, useState } from 'react'
import { Input } from 'almanac-ui/TextInputField/Input'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Modal } from 'almanac-ui/Modal'
import { ConfirmModal, useConfirmModal } from 'hooks/useConfirmModal'
import { useRootStore } from 'stores/RootStore'
import { ArrowRefresh3 } from 'blaze-ui/icons'
import { ValidationBanner } from 'components/ValidationBanner/ValidationBanner'
import {
  getCharacterLimitViolations,
  PLATFORM_CHARACTER_LIMITS,
  getCharacterCount,
} from 'components/ValidationBanner/utils'
import { DesignChannel } from 'components/NewFileModal/Types'
import cx from 'classnames'
import { mapDesignChannelToPlatform } from './utils'

const ConfirmRemove = ({ onConfirm, onCancel }: ConfirmModal) => {
  return (
    <Modal.Root size="xs">
      <Modal.Header title="Delete Caption" />
      <Modal.Content>
        <p>Are you sure you want to delete the caption for this design?</p>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="sm" variant="tertiary" onPress={onCancel}>
            No
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="sm" onPress={onConfirm}>
            Yes
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

type Props = {
  caption: string
  setCaption: (caption: string) => void
  insertCaption: () => void
  removeCaption: () => void
  setRegenerating: (isRegenerating: boolean) => void
  channel?: DesignChannel // For validation banner
}

export const CaptionEdit = (props: Props) => {
  const { drawersStore, designStore } = useRootStore()
  const captionsStore = designStore?.captionsStore
  const confirm = useConfirmModal(ConfirmRemove)
  const { caption, setCaption, insertCaption, removeCaption, setRegenerating, channel } = props
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const { isMobile } = useResponsiveDesign()

  const loading = saving || removing

  const selectedPlatform = channel ? mapDesignChannelToPlatform(channel) : undefined

  const hasCharacterLimitError = selectedPlatform
    ? getCharacterLimitViolations(caption, [selectedPlatform]).length > 0
    : false

  // Get the character limit for the selected platform
  const maxCharacterCount = selectedPlatform ? PLATFORM_CHARACTER_LIMITS[selectedPlatform]?.limit : undefined

  useEffect(() => {
    captionsStore?.setSectionTitle('Edit Caption')
  }, [captionsStore])

  const handleSave = async () => {
    if (!saving) {
      setSaving(true)
      await insertCaption()
      setSaving(false)
    }
  }

  const handleRemove = async () => {
    drawersStore.closeDrawer()

    if (await confirm()) {
      if (!removing) {
        setRemoving(true)
        await removeCaption()
        setRemoving(false)
        setCaption('')
      }
    }
  }

  return (
    <div className={Styles.form}>
      <div className={Styles.field}>
        <span>
          Post Caption <span className={Styles.required}>*</span>
        </span>
        <div
          className={cx(Styles.inputWrapper, {
            [Styles.error]: hasCharacterLimitError,
          })}
        >
          <Input
            className={Styles.textArea}
            aria-label="Post caption"
            name="Caption prompt"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write your post caption here..."
            isTextarea
            rows={isMobile ? 10 : 3}
            size={isMobile ? 'md' : 'sm'}
            withBorder={!hasCharacterLimitError}
          />
          {maxCharacterCount && (
            <div className={Styles.footerRight}>
              Character Count{' '}
              <span
                className={cx({
                  [Styles.exceededLimit]: getCharacterCount(caption) > maxCharacterCount,
                })}
              >
                {getCharacterCount(caption)}
              </span>
              /{maxCharacterCount}
            </div>
          )}
        </div>

        {selectedPlatform && (
          <ValidationBanner content={caption} selectedPlatform={selectedPlatform} className={Styles.validationBanner} />
        )}

        <div className={Styles.buttons}>
          <Button
            variant="tertiary"
            frontIcon={ArrowRefresh3}
            isDisabled={loading}
            size={isMobile ? 'lg' : 'md'}
            onPress={() => setRegenerating(true)}
          >
            Regenerate Caption
          </Button>
          <Button variant="secondary" isDisabled={loading} size={isMobile ? 'lg' : 'md'} onPress={handleRemove}>
            {removing ? 'Deleting...' : 'Delete Caption'}
          </Button>
        </div>

        <div className={Styles.footer}>
          <Button
            isDisabled={loading || getCharacterCount(caption.trim()) === 0 || hasCharacterLimitError}
            size={isMobile ? 'lg' : 'md'}
            onPress={handleSave}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}
